import { Body2, Body3 } from "@iventis/styles/src/components/texts";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SimpleTooltip } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import React, { FunctionComponent, useState } from "react";
import { Content } from "@iventis/translations";
import { changeLogItem } from "./change-log-item-types";
import { formatLocalTime } from "./change-log-helpers";

type ChangeLogItemProps = { changeLogItem: changeLogItem; className?: string };

const sanitizeToAlphabetic = (value: string) => value.replace(/[^a-z ]/gi, "").toLowerCase();

export const ChangeLogItemComponent: FunctionComponent<ChangeLogItemProps> = ({ changeLogItem, className }) => {
    const translate = useIventisTranslate();
    const [open, setOpen] = useState(false);
    const isDotBackgroundAction = changeLogItem.action === "unlinked" || changeLogItem.action === "copied" || changeLogItem.action === "imported";
    const sanitizedTitle = sanitizeToAlphabetic(changeLogItem.title);
    const sanitizedAction = sanitizeToAlphabetic(changeLogItem.action);
    const changeLogItemTestId = `change-log-item-${sanitizedTitle}-${sanitizedAction}`;
    const fullDateTimeTooltip = new Intl.DateTimeFormat("en-GB", {
        dateStyle: "full",
        timeStyle: "medium",
    }).format(new Date(changeLogItem.time));

    let dotForegroundIcon: React.ComponentProps<typeof FontAwesomeIcon>["icon"] = ["far", "link-slash"];

    if (changeLogItem.action === "copied") {
        dotForegroundIcon = ["fas", "copy"];
    } else if (changeLogItem.action === "imported") {
        dotForegroundIcon = ["fas", "file-import"];
    }
    return (
        <div data-testid={changeLogItemTestId} className={className}>
            <StyledHeader>
                <StyledHeaderItem>
                    <TitleContainer>
                        {isDotBackgroundAction ? (
                            <ActionIconContainer>
                                <ActionBackgroundDot icon={["fas", "circle"]} />
                                <ActionForegroundIcon icon={dotForegroundIcon} />
                            </ActionIconContainer>
                        ) : (
                            <DotIcon
                                icon={["fas", changeLogItem.action === "create" ? "circle-plus" : changeLogItem.action === "delete" ? "circle-minus" : "pen-circle"]}
                                action={changeLogItem.action as string}
                            />
                        )}
                        <TitleText data-testid="change-log-title">{changeLogItem.title}</TitleText>
                        <SubTitle data-testid="change-log-sub-title">{changeLogItem.subTitle}</SubTitle>
                    </TitleContainer>
                </StyledHeaderItem>
                <StyledHeaderItem>
                    <StyledUserText>{changeLogItem.userName}</StyledUserText>
                    <SimpleTooltip text={fullDateTimeTooltip} placement="top">
                        <span>
                            <StyledTimeText data-testid="change-log-time">{formatLocalTime(changeLogItem.time)}</StyledTimeText>
                        </span>
                    </SimpleTooltip>
                </StyledHeaderItem>
            </StyledHeader>
            <DetailsWrapper>
                {changeLogItem.details && changeLogItem.details.length > 0 && (
                    <>
                        <DetailsToggle data-testid="change-log-details-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                            <ArrowIcon icon={["fas", open ? "chevron-down" : "chevron-right"]} />
                            <ToggleText>
                                {translate(Content.changeLog.labels.viewDetails)} ({changeLogItem.details.length})
                            </ToggleText>
                        </DetailsToggle>
                        {open && (
                            <DetailsList data-testid="change-log-details">
                                {changeLogItem.details.map((detail) => (
                                    <li key={detail} data-testid="change-log-detail-item">
                                        <DetailItemText>{detail}</DetailItemText>
                                    </li>
                                ))}
                            </DetailsList>
                        )}
                    </>
                )}
            </DetailsWrapper>
        </div>
    );
};

const StyledUserText = styled(Body2)`
    text-align: right;
    color: ${({ theme }) => theme.typographyColors.subdued};
`;

const StyledTimeText = styled(Body2)`
    text-align: right;
    color: ${({ theme }) => theme.typographyColors.subdued};
`;

const StyledHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const StyledHeaderItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const TitleText = styled(Body2)`
    font-weight: bold;
    color: ${({ theme }) => theme.typographyColors.lessSubdued};
`;

const TitleContainer = styled.div`
    display: grid;
    grid-template-columns: 10px 1fr;
    column-gap: 8px;
    align-items: center;
`;

const DotIcon = styled(FontAwesomeIcon)<{ action: string }>`
    font-size: 14px;
    color: ${({ action }) => (action === "create" ? "green" : action === "delete" ? "red" : action === "update" ? "orange" : "gray")};
    align-self: center;
`;

const ActionIconContainer = styled.div`
    position: relative;
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ActionBackgroundDot = styled(FontAwesomeIcon)`
    position: absolute;
    font-size: 14px;
    color: green;
`;

const ActionForegroundIcon = styled(FontAwesomeIcon)`
    position: absolute;
    font-size: 10px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.8);
    transform-origin: center;
    color: ${({ theme }) => theme.secondaryColors.blank};
`;

const SubTitle = styled(Body2)`
    grid-column: 2;
`;

const DetailsToggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.primaryColors.altFocusText};
    cursor: pointer;
    padding: 4px 0;
`;

const ToggleText = styled.span`
    font-size: 0.9rem;
`;

const ArrowIcon = styled(FontAwesomeIcon)`
    width: 12px;
`;

const DetailsList = styled.ul`
    margin: 8px 0 0 18px;
    padding-left: 20px;
    max-height: 160px;
    overflow-y: auto;
    overflow-x: hidden;
    list-style: disc;
    list-style-position: outside;

    & > li {
        display: list-item;
        list-style-type: disc;
        margin: 4px 0;
        color: inherit;
    }

    & > li::marker {
        color: currentColor;
    }
`;

const DetailItemText = styled(Body3)`
    margin: 0;
`;

const DetailsWrapper = styled.div`
    margin-left: 18px; /* align with Title text column (10px icon + 8px gap) */
`;
